import {
  getPostHogClient,
  readManualOverrideFlags,
  readOverrideFlags,
  setManualFlagOverride,
} from '@listening/frontend/analytics/client';
import type { EdgeBucketedFlagKey } from '@listening/frontend/analytics/define-route-analytics';
import { ClientFlags } from '@listening/frontend/analytics/flags/client';
import type { FeatureFlagDef } from '@listening/frontend/analytics/flags/types';
import { DialogDropdown } from '@listening/frontend/components/custom/dialog-dropdown';
import { TooltipButton } from '@listening/frontend/components/custom/tooltip-button';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@listening/frontend/components/radix/dropdown-menu';
import { ClientOnly } from '@tanstack/react-router';
import { TriangleFlag, Undo } from 'iconoir-react';
import { useEffect, useState } from 'react';
import { clientEnv } from '@/env';
import { Predicate } from 'effect';

type OverrideFlags = Record<string, string | boolean>;

export function DocsExperimentSelector({
  experimentNames,
  onOpenChange,
}: {
  experimentNames: ReadonlyArray<EdgeBucketedFlagKey>;
  onOpenChange?: ((open: boolean) => void) | undefined;
}) {
  if (clientEnv.mode === 'production') return null;

  return (
    <ClientOnly>
      <DocsExperimentSelectorClient
        experimentNames={experimentNames}
        onOpenChange={onOpenChange}
      />
    </ClientOnly>
  );
}

function DocsExperimentSelectorClient({
  experimentNames,
  onOpenChange,
}: {
  experimentNames: ReadonlyArray<EdgeBucketedFlagKey>;
  onOpenChange?: ((open: boolean) => void) | undefined;
}) {
  const [overrides, setOverrides] = useState(readManualOverrideFlags);
  const experimentKeys = new Set<string>(
    experimentNames.map((name) => ClientFlags[name].key),
  );
  const hasOverrides = Object.keys(overrides).some((key) =>
    experimentKeys.has(key),
  );

  useEffect(() => {
    let mounted = true;
    const unsubscribe = getPostHogClient().onFeatureFlags(() => {
      queueMicrotask(() => {
        if (mounted) setOverrides(readManualOverrideFlags());
      });
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const writeOverrides = (nextOverrides: OverrideFlags) => {
    if (Object.keys(nextOverrides).length === 0) {
      getPostHogClient().featureFlags.overrideFeatureFlags(false);
    } else {
      getPostHogClient().featureFlags.overrideFeatureFlags({
        flags: nextOverrides,
      });
    }
    setOverrides(readManualOverrideFlags());
  };

  const setOverride = (flagKey: string, value: string) => {
    if (value) {
      setManualFlagOverride(flagKey, value);
      setOverrides(readManualOverrideFlags());
      return;
    }

    const { [flagKey]: _, ...remainingOverrides } = readOverrideFlags();
    writeOverrides(remainingOverrides);
  };

  const resetDocsOverrides = () => {
    writeOverrides(
      Object.fromEntries(
        Object.entries(readOverrideFlags()).filter(
          ([key]) => !experimentKeys.has(key),
        ),
      ),
    );
  };

  return (
    <DialogDropdown<HTMLButtonElement>
      align='end'
      className='w-[min(360px,calc(100vw-1rem))] rounded-xl border border-[#e3ebf7] bg-white p-0 shadow-xl dark:border-white/10 dark:bg-slate-900'
      {...(onOpenChange && { onOpenChange })}
      trigger={({ ref }) => (
        <TooltipButton
          ref={ref}
          aria-label='Feature flags'
          toolTipContent='Feature flags'
          variant='ghost'
          className='relative h-auto p-2 text-muted-foreground'
        >
          <TriangleFlag className='size-5' />
          {hasOverrides && (
            <span className='absolute top-1 right-1 size-2 rounded-full bg-amber-500 ring-2 ring-white dark:ring-slate-900' />
          )}
        </TooltipButton>
      )}
    >
      <div className='px-4 pt-3 pb-2'>
        <div className='flex items-center justify-between gap-3'>
          <span className='text-sm font-semibold text-[#1a2c4e] dark:text-slate-100'>
            Docs experiments
          </span>
          <span className='rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-amber-800 uppercase dark:bg-amber-950 dark:text-amber-300'>
            QA only
          </span>
        </div>
        <p className='mt-1 text-xs text-muted-foreground'>
          Overrides stay on this device and update the page immediately.
        </p>
      </div>

      <DropdownMenuSeparator />

      {experimentNames.length === 0 ? (
        <p className='px-4 py-4 text-sm text-muted-foreground'>
          No active experiments on this page.
        </p>
      ) : (
        <div className='max-h-[60vh] space-y-3 overflow-y-auto px-4 py-3'>
          {experimentNames.map((name) => {
            // Widened to the base def: `label`/`variantLabels` are optional on
            // `FeatureFlagDef` and only the public-doc tests declare them, so
            // reading them off the `as const` union is a type error. The
            // funnel and pricing experiments fall back to their key and raw
            // variant names, which is all this QA panel needs.
            const definition: FeatureFlagDef = ClientFlags[name];
            const override = overrides[definition.key];

            return (
              <label className='block space-y-1.5' key={name}>
                <span className='block text-xs font-medium text-[#1a2c4e] dark:text-slate-200'>
                  {definition.label ?? definition.key}
                </span>
                <select
                  aria-label={`Override ${definition.key}`}
                  className='h-9 w-full rounded-md border border-[#d9e2ef] bg-white px-2 text-sm text-[#1a2c4e] outline-hidden focus:border-[#526cff] focus:ring-2 focus:ring-[#526cff]/20 dark:border-white/15 dark:bg-slate-950 dark:text-slate-100'
                  onChange={(event) =>
                    setOverride(definition.key, event.target.value)
                  }
                  value={Predicate.isString(override) ? override : ''}
                >
                  <option value=''>Automatic (A/B assignment)</option>
                  {definition.variants.map((variant) => (
                    <option key={variant} value={variant}>
                      {definition.variantLabels?.[variant] ?? variant}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
        </div>
      )}

      {experimentNames.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className='m-1.5 gap-2 text-xs'
            disabled={!hasOverrides}
            onSelect={resetDocsOverrides}
          >
            <Undo className='size-3.5' />
            Reset Docs overrides
          </DropdownMenuItem>
        </>
      )}
    </DialogDropdown>
  );
}
